import { DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useContext, useState } from 'react';

import {
  TransportationMode,
  TransportationModeValues,
} from '@/domain/impact-co2/entities/question.entity';
import { VenueDefaultDataMobility } from '@/domain/project-venue/entities/project-venue.entity';
import { VenuePreset } from '@/domain/project-venue-presets/entities/project-venue-presets.entity.ts';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { thStyle } from '@/ui/components/table/Table.utils';
import { translate } from '@/ui/i18n';
import { DefaultDatasContext } from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasContext';
import {
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/project/venue/default-datas/default-datas-quiz-panel/DefaultDatasQuizComponents';
import { ColorsRaw } from '@/ui/theme/colors-raw';

export function MobilityForm({
  onEdit,
  venueDefaultDatas,
  forceEdit,
  onClose,
}: {
  onEdit(venueDatas: VenuePreset): void;
  venueDefaultDatas: VenuePreset;
  forceEdit?: boolean;
  onClose?: () => void;
}) {
  const { panelOpen } = useContext(DefaultDatasContext);
  const [isEditing, setIsEditing] = useState(forceEdit ? true : false);
  const [values, setValues] = useState(
    structuredClone(venueDefaultDatas.mobility),
  );

  const disableSubmitActions = false; //TODO

  const addTransportToAnswer = (transport: TransportationMode) => {
    setValues([...values, { type: transport, percentage: 0, distance: 0 }]);
  };

  const updateTransportDistance = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index].distance = Number(value);
    if (!isNaN(newValues[index].distance)) {
      setValues(newValues);
    }
  };

  const updateTransportPercentage = (value: string, index: number) => {
    const newValues = [...values];
    newValues[index].percentage = Number(Number(value).toFixed(2));
    if (!isNaN(newValues[index].percentage)) {
      setValues(newValues);
    }
  };

  const removeTransport = (index: number) => {
    const newValues = [...values];
    newValues.splice(index, 1);
    setValues(newValues);
  };

  const handleEdit = () => {
    onEdit({ ...venueDefaultDatas, mobility: values });
    if (!forceEdit) setIsEditing(false);
  };

  const handleRemoveClick = () => {
    if (onClose) onClose();
    else setIsEditing(false);
  };

  return (
    <div id="mobility-form">
      {(isEditing && !!panelOpen) || !!forceEdit ? (
        <QuizEdition
          removeFromEditing={handleRemoveClick}
          title={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.what-mobility-distribution`,
          )}
          subTitle={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.add-mobility-type`,
          )}
          form={
            <Form
              onSubmit={handleEdit}
              disableSubmit={disableSubmitActions}
              addTransportToAnswer={addTransportToAnswer}
              updateTransportDistance={updateTransportDistance}
              updateTransportPercentage={updateTransportPercentage}
              removeTransport={removeTransport}
              values={values}
            />
          }
        />
      ) : (
        <QuizNotEdition
          addToEditing={() => setIsEditing(true)}
          answeredValues={values.map((value) => {
            return `${translate(`ProjectsVenue.DefaultDatas.Public.Forms.Mobility.TransportationModeValues.${value.type}`)}: ${value.percentage}%, ${value.distance}km`;
          })}
          title={translate(
            `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.what-mobility-distribution`,
          )}
          isAnswered={values.length > 0}
        />
      )}
    </div>
  );
}

function Form({
  addTransportToAnswer,
  updateTransportDistance,
  updateTransportPercentage,
  removeTransport,
  disableSubmit,
  onSubmit,
  values,
}: {
  onSubmit: () => void;
  addTransportToAnswer: (transport: TransportationMode) => void;
  disableSubmit: boolean;
  updateTransportDistance: (value: string, index: number) => void;
  updateTransportPercentage: (value: string, index: number) => void;
  removeTransport: (index: number) => void;
  values: VenueDefaultDataMobility;
}) {
  const [selectedTransport, setSelectedTransport] = useState<
    TransportationMode | ''
  >('');
  const [percentageError, setPercentageError] = useState(false);

  const getSelectValues = () => {
    const selectedTransports = values.map((value) => value.type);

    return selectedTransports
      ? TransportationModeValues.filter(
          (acc) => !selectedTransports.includes(acc),
        )
      : TransportationModeValues;
  };

  const addTransport = () => {
    if (selectedTransport) {
      addTransportToAnswer(selectedTransport);
      setSelectedTransport('');
    }
  };

  const handleChangePercentage = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    setPercentageError(false);
    updateTransportPercentage(e.target.value, index);
  };

  const handleSubmit = () => {
    const percentages = values.map((val) => val.percentage);

    const totalPercentage =
      percentages.length > 0
        ? percentages.reduce((acc, value) => {
            return acc + value;
          })
        : 0;

    if (totalPercentage === 100) {
      onSubmit();
    } else {
      setPercentageError(true);
    }
  };

  return (
    <Stack>
      <Table align="right">
        <TableHead>
          <TableRow>
            <TableCell style={thStyle}>
              {translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.mobility-type`,
              )}
            </TableCell>
            <TableCell style={thStyle}>
              {`${translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.distribution`,
              )} (%)`}
            </TableCell>
            <TableCell style={thStyle}>
              {`${translate(
                `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.distance`,
              )} (km)`}
            </TableCell>
            <TableCell style={thStyle} />
          </TableRow>
        </TableHead>
        <TableBody>
          {values?.map((value, index) => (
            <TableRow key={'selectedTransport' + index}>
              <TableCell>
                {translate(
                  `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.TransportationModeValues.${value.type}`,
                )}
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <Box display="flex" alignItems="center">
                  <FSTextField
                    sx={{ width: '80px' }}
                    type="number"
                    textAlign="right"
                    value={value.percentage}
                    onChange={(e) => handleChangePercentage(e, index)}
                    error={percentageError}
                  />
                  <Typography sx={{ ml: 1 }} fontSize={16}>
                    %
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ py: 0 }}>
                <Box display="flex" alignItems="center">
                  <FSTextField
                    sx={{ width: '80px' }}
                    type="number"
                    textAlign="right"
                    value={value.distance}
                    onChange={(e) =>
                      updateTransportDistance(e.target.value, index)
                    }
                  />
                  <Typography sx={{ ml: 1 }} fontSize={16}>
                    km
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ px: 0 }}>
                <Box display="flex" justifyContent="end">
                  <Button onClick={() => removeTransport(index)}>
                    <DeleteOutline sx={{ color: ColorsRaw.basic.White }} />
                  </Button>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={4}>
              <Box display={'flex'} alignItems={'center'}>
                <FSTextField
                  select
                  label={translate(
                    'ProjectsVenue.DefaultDatas.Public.Forms.Mobility.transportation-mode',
                  )}
                  value={selectedTransport}
                  onChange={(e) =>
                    setSelectedTransport(e.target.value as TransportationMode)
                  }
                  sx={{ width: '100%' }}
                >
                  {getSelectValues().map((value) => (
                    <MenuItem key={value} value={value}>
                      {translate(
                        `ProjectsVenue.DefaultDatas.Public.Forms.Mobility.TransportationModeValues.${value}`,
                      )}
                    </MenuItem>
                  ))}
                </FSTextField>

                <FSPrimaryButton sx={{ ml: 1 }} onClick={addTransport}>
                  {translate('Form.add')}
                </FSPrimaryButton>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {percentageError && (
        <Typography sx={{ color: ColorsRaw.error.Error, mt: 2 }}>
          {translate(
            'ProjectsVenue.DefaultDatas.Public.Forms.Mobility.percentage-error',
          )}
        </Typography>
      )}

      <Box marginTop={2}>
        <FSPrimaryButton
          type="submit"
          disabled={disableSubmit}
          onClick={handleSubmit}
        >
          {translate('Form.save')}
        </FSPrimaryButton>
      </Box>
    </Stack>
  );
}
